import type { ClientGroup } from '@DigitaleDoerfer/digitale-doerfer-api';
import {
  ClientGroupAccessibilityEnum,
  ClientGroupMembershipStatusEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, IconButton, Text } from 'react-native-paper';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useJoinGroup } from '@/features/api/hooks/grapevine-group-events/useJoinGroup';
import { useFlag } from '@/features/flag/providers/useFlag';
import { FlagEntityTypeEnum } from '@/features/flag/types/flagEntityType';
import { GroupsRoute } from '@/features/groups/navigation/groupsRoute';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { getSizedMediaItemUrl } from '@/features/media/utils/mediaItem';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationButton } from '@/navigation/components/NavigationButton';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import {
  AppRoute,
  type AppNativeStackScreenProps
} from '@/navigation/types/navigation';
import { StateView } from '@/shared/components/state-view/StateView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    wrapper: {
      backgroundColor: theme.colors.surfaceContainerLow,
      paddingBottom: insets.bottom,
      paddingHorizontal: 16,
      alignItems: 'center'
    },
    image: { margin: -8 },
    text: { textAlign: 'center' },
    gap: {
      marginTop: 32
    },
    buttonGroup: {
      gap: 16
    }
  });

type GroupDescriptionProps = {
  group: ClientGroup;
};

const GroupDescription: FC<GroupDescriptionProps> = ({ group }) => {
  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'screens.group'
  });
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);
  const navigation = useAppNavigation();

  const { description, name, logo, shortName, memberCount } = group;

  const handlePicturePress = useCallback(() => {
    const pictureUrl = getSizedMediaItemUrl(
      group?.logo,
      MediaItemSize.ORIGINAL
    )?.url;

    if (isEmptyOrNull(pictureUrl)) return;
    navigation.navigate(AppRoute.Gallery, {
      mediaUrls: [pictureUrl]
    });
  }, [navigation, group]);

  return (
    <View style={{ paddingTop: 16, alignItems: 'center', gap: 16 }}>
      <IconButton
        size={200}
        style={styles.image}
        onPress={logo ? handlePicturePress : undefined}
        icon={() => (
          <ProfileAvatar
            viewSize={200}
            mediaItem={logo}
            textFallback={{ shortName }}
            mediaSize={MediaItemSize.MEDIUM}
          />
        )}
      />
      <View style={{ alignItems: 'center' }}>
        <Text variant="headlineMedium" style={styles.text}>
          {name}
        </Text>
        <Text variant="bodyLarge" style={styles.text}>
          {t('members', { count: memberCount })}
        </Text>
        {!isEmptyOrNull(description) && (
          <Text variant="bodyLarge" style={[styles.text, styles.gap]}>
            {description}
          </Text>
        )}
      </View>
    </View>
  );
};

export type Props = AppNativeStackScreenProps<typeof AppRoute.GroupDescription>;

export const GroupDescriptionScreen: FC<Props> = ({ route }) => {
  const { groupId } = route.params;

  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'screens.description'
  });
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);

  const { flag } = useFlag();

  const { data: group, isFetching } = useGetGroup(groupId);

  const requiresApproval =
    group?.accessibility === ClientGroupAccessibilityEnum.ApprovalRequired;

  const joinGroup = useJoinGroup();
  const handleJoinRequest = useCallback(
    () => joinGroup.mutate({ groupId }),
    [groupId, joinGroup]
  );

  // TODO: handle group not found case
  return (
    group && (
      <StateView isLoading={isFetching} contentContainerStyle={styles.wrapper}>
        <GroupDescription group={group} />
        <View style={[styles.buttonGroup, styles.gap]}>
          {group?.membershipStatus ===
            ClientGroupMembershipStatusEnum.NotMember &&
            (requiresApproval ? (
              <NavigationButton
                screen={GroupsRoute.GroupRequest}
                mode="contained"
                params={{ groupId }}
              >
                {t('join')}
              </NavigationButton>
            ) : (
              <Button onPress={handleJoinRequest} mode="contained">
                {t('join')}
              </Button>
            ))}
          <Button onPress={() => flag(FlagEntityTypeEnum.Group, groupId)}>
            {t('reportViolation')}
          </Button>
        </View>
      </StateView>
    )
  );
};
